import { useEffect, useState } from 'react';
import { Button, Flex, Form, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import globalNotification from '../../common/hooks/globalNotification';
import { notificationType } from '../../common/constants/notificationType';
import { useVinExtractionService } from '../../services/vinExtractionService';
import { changedSettingsFields, isVersionConflict, parseSettings } from '../../utils/vinExtraction';
import { radius, spacing } from '../../theming';
import { labelStyle } from './vinExtractionStyles';
import { notifyVinError } from './vinExtractionErrors';
import SettingsConflictPrompt from './SettingsConflictPrompt';
import SettingsFields from './SettingsFields';
import SettingsProcessingCard from './SettingsProcessingCard';
import TestBatchCard from './TestBatchCard';

const formValues = settings => ({
    confidenceThreshold: settings.confidenceThreshold,
    batchCarLimit: settings.batchCarLimit,
    model: settings.model ?? undefined,
    thinkingLevel: settings.thinkingLevel ?? undefined,
});

/** The form, opened from the current settings; a 409 shows the conflict prompt, whose Reload starts it over. */
const useSettingsForm = ({ open, settings, onReload }) => {
    const [form] = Form.useForm();
    const [base, setBase] = useState(settings);
    const [conflict, setConflict] = useState(false);
    const [reloading, setReloading] = useState(false);

    const startFrom = values => {
        setBase(values);
        setConflict(false);
        form.setFieldsValue(formValues(values));
    };

    useEffect(() => {
        if (open) startFrom(settings);
        // only on open: later settings changes (e.g. from the switch) must not wipe what the admin typed
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const reload = async () => {
        setReloading(true);
        const fresh = await onReload();
        setReloading(false);
        if (fresh) startFrom(fresh);
    };

    return { form, base, conflict, setConflict, reloading, reload };
};

/** Save: PATCH only the changed fields with the base version; nothing changed just closes. */
const useSettingsSave = ({ form, base, setConflict, onClose, onSettingsChange, onReload }) => {
    const { t } = useTranslation('vinExtraction');
    const { updateSettings } = useVinExtractionService();
    const [saving, setSaving] = useState(false);

    const save = async () => {
        const body = changedSettingsFields(base, await form.validateFields());
        if (!body) return onClose();
        setSaving(true);
        try {
            const next = parseSettings(await updateSettings(body));
            if (next) onSettingsChange(next);
            else await onReload();
            globalNotification.open({ type: notificationType.SUCCESS, message: t('edit.saved'), placement: 'top' });
            onClose();
        } catch (error) {
            if (isVersionConflict(error)) setConflict(true);
            else notifyVinError(t, error);
        } finally {
            setSaving(false);
        }
    };

    return { saving, save };
};

const footerButtons = ({ t, onClose, saving, saveDisabled, onSave }) => [
    <Button key="cancel" onClick={onClose}>
        {t('edit.cancel')}
    </Button>,
    <Button key="save" type="primary" loading={saving} disabled={saveDisabled} onClick={onSave}>
        {t('edit.save')}
    </Button>,
];

/**
 * Edit: the Processing switch and the one-batch run (both applied immediately) and the PATCH settings (applied on Save, versioned).
 * Save diffs against the values the form opened with and sends their version, so a concurrent edit is a 409
 * with a reload prompt (R1), never a silent overwrite.
 */
export default function SettingsEditModal({ open, settings, readOnly, onClose, onSettingsChange, onReload }) {
    const { t } = useTranslation('vinExtraction');
    const editor = useSettingsForm({ open, settings, onReload });
    const { form, conflict } = editor;
    const { saving, save } = useSettingsSave({ ...editor, onClose, onSettingsChange, onReload });
    const onSave = () => save().catch(() => {});

    return (
        <Modal
            open={open}
            title={t('edit.title')}
            onCancel={onClose}
            destroyOnClose
            // App.scss zeroes the padding of every modal; the app's padded dialogs use the same spacing
            styles={{ content: { borderRadius: radius.lg, padding: `${spacing[5]} ${spacing[4]}` } }}
            footer={footerButtons({ t, onClose, saving, saveDisabled: readOnly || conflict, onSave })}
        >
            <Flex vertical gap={spacing[3]}>
                <SettingsProcessingCard
                    settings={settings}
                    readOnly={readOnly}
                    onSettingsChange={onSettingsChange}
                    onReload={onReload}
                />
                <TestBatchCard settings={settings} readOnly={readOnly} onReload={onReload} />
                {conflict && <SettingsConflictPrompt reloading={editor.reloading} onReload={editor.reload} />}
                <Form
                    form={form}
                    layout="vertical"
                    disabled={readOnly}
                    initialValues={formValues(settings)}
                    requiredMark={false}
                >
                    <SettingsFields settings={settings} />
                </Form>
                <span style={labelStyle}>{t('edit.appliesNext')}</span>
            </Flex>
        </Modal>
    );
}
